import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, TimeoutError, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { environment } from 'environments/environment';
import { ApiError, Paging } from '../models/base.model';

// *********** Type Definitions **************

/**
 * Interface for models that have a static parse method
 */
export interface Parseable<T> {
  parse(data: any): T | null;
}

/**
 * Type for parser function
 */
export type ParserFunction<T> = (data: any) => T | null;

/**
 * Interface for paginated API responses
 */
export interface PaginatedResponse<T> {
  data: T[];
  page: Paging;
}

// *********** Error Handler **************

class ErrorHandlerProvider {

  constructor() {
  }

  public handleError(error: HttpErrorResponse | any): Observable<ApiError | any> {
    try {
      let err: ApiError;
      console.error('Error : ', error);

      if (error && error.error && error.error.error) {
        const temp = error.error.error;
        let message = `${temp.description}`;
        err = new ApiError(message, temp.code, temp.description, temp.error_code ? temp.error_code : '', error);
        // err = {
        //   code: temp.code,
        //   description: temp.description,
        //   code_r: temp.error_code ? temp.error_code : ''
        // };
      } else {

        err = new ApiError('Uncommon error, no internet?', error.status, error.statusText, `HTTP_ERROR_${error.status}`, error);
        // err = {
        //   code: error.status,
        //   description: error.statusText,
        //   code_r: `HTTP_ERROR_CODE_${error.status}`
        // };

      }
      // console.log('Define error : ', err);
      return throwError(() => err);
    } catch (catchError) {
      let err = new ApiError('Unknown error while constructing error', error.status, error.statusText, `CATCH_ERROR_${error.status}`, error);
      return throwError(() => err);
    }
  }

  public managerErrorHandler(rawErrorData) {

    let error;
    if (rawErrorData instanceof TimeoutError) {
      error = {
        code: 0,
        desc: 'Timeout.'
      };
    } else {
      error = rawErrorData;
    }

    return error;
  }
}

// *********** Base API Service **************

@Injectable()
export class RestApiService {
  errorHandler: ErrorHandlerProvider = new ErrorHandlerProvider();
  constructor(public http: HttpClient) {
    // if(environment.production) {
    //   this.hostingType = HOSTINGTYPE.production;
    // } else {
    //   this.hostingType = HOSTINGTYPE.staging;
    // }

  }

  // *********** Configuration Methods **************

  protected getBaseUrl(): string {
    return environment.apiHostUrl;
  }

  protected getClientID(): string | null | undefined {
    try {
      return environment.auth.clientID;
    } catch (error) {
      return null;
    }
  }

  /**
   * Helper method to build HttpParams from an object
   */
  protected buildHttpParams(params: any = {}): HttpParams {
    return new HttpParams({ fromObject: params });
  }

  // *********** Generic Response Parser Methods **************

  /**
   * Handles parsing of a single object response
   * @param observable - The HTTP observable to process
   * @param parser - Function that parses the raw response data into a typed model
   * @returns Observable of the parsed model
   *
   * @example
   * return this.requestSingle(
   *   this.http.get(url, { params }),
   *   Company.parse
   * );
   */
  protected requestSingle<T>(
    observable: Observable<any>,
    parser: ParserFunction<T>
  ): Observable<T> {
    return observable.pipe(
      map(response => {
        if (!response) {
          throw new Error('Response is null or undefined');
        }
        const parsed = parser(response);
        if (!parsed) {
          throw new Error('Failed to parse response');
        }
        return parsed;
      }),
      catchError(this.errorHandler.handleError)
    );
  }

  /**
   * Handles parsing of paginated list responses
   * @param observable - The HTTP observable to process
   * @param parser - Function that parses each item in the data array
   * @returns Observable of paginated response with parsed data array
   *
   * @example
   * return this.requestList(
   *   this.http.get(url, { params }),
   *   Company.parse
   * );
   */
  protected requestList<T>(
    observable: Observable<any>,
    parser: ParserFunction<T>
  ): Observable<PaginatedResponse<T>> {
    return observable.pipe(
      map(response => {
        if (!response) {
          throw new Error('Response is null or undefined');
        }

        // Parse pagination info
        let page: Paging | null = null;
        if (response.page) {
          page = Paging.parse(response.page);
          if (!page) {
            throw new Error('Failed to parse pagination data');
          }
        } else {
          throw new Error('Response missing page data');
        }

        // Parse data array
        const data: T[] = [];
        if (response.data && Array.isArray(response.data)) {
          for (const item of response.data) {
            const parsed = parser(item);
            if (parsed !== null) {
              data.push(parsed);
            }
          }
        }

        return { data, page };
      }),
      catchError(this.errorHandler.handleError)
    );
  }

  /**
   * Handles parsing of non-paginated array responses
   * @param observable - The HTTP observable to process
   * @param parser - Function that parses each item in the array
   * @returns Observable of array of parsed items
   *
   * @example
   * return this.requestArray(
   *   this.http.get(url, { params }),
   *   Tag.parse
   * );
   */
  protected requestArray<T>(
    observable: Observable<any>,
    parser: ParserFunction<T>
  ): Observable<T[]> {
    return observable.pipe(
      map(response => {
        if (!response) {
          throw new Error('Response is null or undefined');
        }

        const data: T[] = [];
        if (Array.isArray(response)) {
          for (const item of response) {
            const parsed = parser(item);
            if (parsed !== null) {
              data.push(parsed);
            }
          }
        } else {
          throw new Error('Response is not an array');
        }

        return data;
      }),
      catchError(this.errorHandler.handleError)
    );
  }

  /**
   * Handles raw responses that don't need parsing
   * @param observable - The HTTP observable to process
   * @returns Observable of raw response
   *
   * @example
   * return this.requestRaw(
   *   this.http.delete(url, { params })
   * );
   */
  protected requestRaw(observable: Observable<any>): Observable<any> {
    return observable.pipe(
      catchError(this.errorHandler.handleError)
    );
  }

  // *********** High-Level CRUD Helper Methods **************

  /**
   * GET request for a single object with automatic parsing
   * @param endpoint - API endpoint relative to base URL (e.g., 'companies/123/')
   * @param parser - Function that parses the response
   * @param params - Query parameters
   * @returns Observable of parsed model
   *
   * @example
   * return this.getSingle('companies/123/', Company.parse, { fields: 'id,title' });
   */
  protected getSingle<T>(
    endpoint: string,
    parser: ParserFunction<T>,
    params: any = {}
  ): Observable<T> {
    const url = this.getBaseUrl() + endpoint;
    const httpParams = this.buildHttpParams(params);
    return this.requestSingle(
      this.http.get<any>(url, { params: httpParams }),
      parser
    );
  }

  /**
   * GET request for a paginated list with automatic parsing
   * @param endpoint - API endpoint relative to base URL (e.g., 'companies/')
   * @param parser - Function that parses each item in the list
   * @param params - Query parameters
   * @returns Observable of paginated response
   *
   * @example
   * return this.getList('companies/', Company.parse, { page: 1, page_size: 10 });
   */
  protected getList<T>(
    endpoint: string,
    parser: ParserFunction<T>,
    params: any = {}
  ): Observable<PaginatedResponse<T>> {
    const url = this.getBaseUrl() + endpoint;
    const httpParams = this.buildHttpParams(params);
    return this.requestList(
      this.http.get<any>(url, { params: httpParams }),
      parser
    );
  }

  /**
   * GET request for a non-paginated array with automatic parsing
   * @param endpoint - API endpoint relative to base URL
   * @param parser - Function that parses each item in the array
   * @param params - Query parameters
   * @returns Observable of array of parsed items
   *
   * @example
   * return this.getArray('tags/', Tag.parse);
   */
  protected getArray<T>(
    endpoint: string,
    parser: ParserFunction<T>,
    params: any = {}
  ): Observable<T[]> {
    const url = this.getBaseUrl() + endpoint;
    const httpParams = this.buildHttpParams(params);
    return this.requestArray(
      this.http.get<any>(url, { params: httpParams }),
      parser
    );
  }

  /**
   * POST request with automatic response parsing
   * @param endpoint - API endpoint relative to base URL
   * @param payload - Request body
   * @param parser - Function that parses the response
   * @param params - Query parameters
   * @returns Observable of parsed model
   *
   * @example
   * return this.postSingle('companies/', payload, Company.parse);
   */
  protected postSingle<T>(
    endpoint: string,
    payload: any,
    parser: ParserFunction<T>,
    params: any = {}
  ): Observable<T> {
    const url = this.getBaseUrl() + endpoint;
    const httpParams = this.buildHttpParams(params);
    return this.requestSingle(
      this.http.post<any>(url, payload, { params: httpParams }),
      parser
    );
  }

  /**
   * PUT request with automatic response parsing
   * @param endpoint - API endpoint relative to base URL
   * @param payload - Request body
   * @param parser - Function that parses the response
   * @param params - Query parameters
   * @returns Observable of parsed model
   *
   * @example
   * return this.putSingle('companies/123/', payload, Company.parse);
   */
  protected putSingle<T>(
    endpoint: string,
    payload: any,
    parser: ParserFunction<T>,
    params: any = {}
  ): Observable<T> {
    const url = this.getBaseUrl() + endpoint;
    const httpParams = this.buildHttpParams(params);
    return this.requestSingle(
      this.http.put<any>(url, payload, { params: httpParams }),
      parser
    );
  }

  /**
   * PATCH request with automatic response parsing
   * @param endpoint - API endpoint relative to base URL
   * @param payload - Request body
   * @param parser - Function that parses the response
   * @param params - Query parameters
   * @returns Observable of parsed model
   *
   * @example
   * return this.patchSingle('companies/123/', payload, Company.parse);
   */
  protected patchSingle<T>(
    endpoint: string,
    payload: any,
    parser: ParserFunction<T>,
    params: any = {}
  ): Observable<T> {
    const url = this.getBaseUrl() + endpoint;
    const httpParams = this.buildHttpParams(params);
    return this.requestSingle(
      this.http.patch<any>(url, payload, { params: httpParams }),
      parser
    );
  }

  /**
   * DELETE request with no response parsing expected
   * @param endpoint - API endpoint relative to base URL
   * @param params - Query parameters
   * @returns Observable of raw response
   *
   * @example
   * return this.deleteSingle('companies/123/');
   */
  protected deleteSingle(endpoint: string, params: any = {}): Observable<any> {
    const url = this.getBaseUrl() + endpoint;
    const httpParams = this.buildHttpParams(params);
    return this.requestRaw(
      this.http.delete<any>(url, { params: httpParams })
    );
  }

  /**
   * GET request with no response parsing
   * @param endpoint - API endpoint relative to base URL
   * @param params - Query parameters
   * @returns Observable of raw response
   *
   * @example
   * return this.getRaw('health-check/');
   */
  protected getRaw(endpoint: string, params: any = {}): Observable<any> {
    const url = this.getBaseUrl() + endpoint;
    const httpParams = this.buildHttpParams(params);
    return this.requestRaw(
      this.http.get<any>(url, { params: httpParams })
    );
  }

  /**
   * POST request with no response parsing
   * @param endpoint - API endpoint relative to base URL
   * @param payload - Request body
   * @param params - Query parameters
   * @returns Observable of raw response
   *
   * @example
   * return this.postRaw('logout/', payload);
   */
  protected postRaw(endpoint: string, payload: any, params: any = {}): Observable<any> {
    const url = this.getBaseUrl() + endpoint;
    const httpParams = this.buildHttpParams(params);
    return this.requestRaw(
      this.http.post<any>(url, payload, { params: httpParams })
    );
  }

}
